import React, { useMemo, useCallback } from 'react';
import { FilterSelect } from '../components/FilterSelect';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  BarChart, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { getClassificationInfo, formatISODate } from '../components/badges';
import {
  RasffNotification, FilterKey, Filters,
  EMPTY_FILTERS, TIME_FRAMES, CLASS_COLORS, TOOLTIP_STYLE, TOOLTIP_CONTENT_STYLE,
  fetchRasff, applyFilters, countField, classifyType,
  buildClassificationTimeline, buildCountryPairBubbles,
} from '../components/dashboard/rasffUtils';
import { DateRangePicker } from '../components/DateRangePicker';
import {
  useUrlState, UrlStateConfig,
  stringCodec, nullableStringCodec, intCodec, boolCodec,
  enumCodec, daysCodec, pageCodec,
} from '../hooks/useUrlState';
import { usePageMeta } from '../hooks/usePageMeta';

const PAGE_SIZES = [10, 25, 50, 100];

/** URL-synced state for this page. Keys here become URL query params. */
type UrlState = {
  days: number;
  from: string;
  to: string;
  /* 9 filter keys (short URL names) */
  category: string | null;
  hazardCategory: string | null;
  hazard: string | null;
  class: string | null;
  risk: string | null;
  origin: string | null;
  notifying: string | null;
  dist: string | null;
  productType: string | null;
  /* Table state */
  q: string;
  sort: 'date' | 'country' | 'category' | 'classification';
  dir: boolean;   // true = ascending; default false (descending)
  page: number;   // 0-indexed in state, 1-indexed in URL
  size: number;
  /* Chart state — 'auto' picks week/month from the date range */
  bucket: 'month' | 'week' | 'auto';
  compare: boolean;
};

/** Module-level constant — stable identity across renders. */
const URL_CONFIG: UrlStateConfig<UrlState> = {
  days:           daysCodec(30),
  from:           stringCodec(''),
  to:             stringCodec(''),
  category:       nullableStringCodec(),
  hazardCategory: nullableStringCodec(),
  hazard:         nullableStringCodec(),
  class:          nullableStringCodec(),
  risk:           nullableStringCodec(),
  origin:         nullableStringCodec(),
  notifying:      nullableStringCodec(),
  dist:           nullableStringCodec(),
  productType:    nullableStringCodec(),
  q:              stringCodec('', { debounceMs: 300 }),
  sort:           enumCodec(['date', 'country', 'category', 'classification'] as const, 'date'),
  dir:            boolCodec(false, 'asc', 'desc'),
  page:           pageCodec(0),
  size:           intCodec(25),
  bucket:         enumCodec(['month', 'week', 'auto'] as const, 'auto'),
  compare:        boolCodec(false),
};

/** Map the internal FilterKey (from rasffUtils) to its URL param name. */
const FILTER_KEY_TO_URL: Record<FilterKey, keyof UrlState> = {
  category:         'category',
  hazardCategory:   'hazardCategory',
  hazard:           'hazard',
  classification:   'class',
  riskDecision:     'risk',
  originCountry:    'origin',
  notifyingCountry: 'notifying',
  distCountry:      'dist',
  productType:      'productType',
};

export default function RasffDeepDivePage() {
  usePageMeta({ title: 'RASFF deep dive', description: 'Explore EU RASFF notifications by hazard, product category, origin country and risk level over any date range.' });
  /* URL is the source of truth for every filter, sort, page, and search term.
     Deep-links from the Knowledge Center (?hazard=…&days=…) are parsed by the
     same codecs that write back when the user changes a filter. */
  const { state, set, patch } = useUrlState(URL_CONFIG);

  /* Named aliases so downstream JSX/hooks read unchanged. */
  const days         = state.days;
  const dateFrom     = state.from;
  const dateTo       = state.to;
  const searchQuery  = state.q;
  const currentPage  = state.page;
  const pageSize     = state.size;
  const timeBucket   = state.bucket;
  /* 'auto': short ranges bucket by week — a 30d window in month buckets renders
     two bars where the partial month reads as a false decline. Explicit user
     choice (month/week) is kept in the URL and wins. */
  const effectiveBucket: 'month' | 'week' =
    timeBucket === 'auto' ? (days > 0 && days <= 45 ? 'week' : 'month') : timeBucket;
  const comparePrev  = state.compare;
  const sortCol      = state.sort;
  const sortAsc      = state.dir;

  const filters: Filters = useMemo(() => ({
    category:         state.category,
    hazardCategory:   state.hazardCategory,
    hazard:           state.hazard,
    classification:   state.class,
    riskDecision:     state.risk,
    originCountry:    state.origin,
    notifyingCountry: state.notifying,
    distCountry:      state.dist,
    productType:      state.productType,
  }), [
    state.category, state.hazardCategory, state.hazard, state.class,
    state.risk, state.origin, state.notifying, state.dist, state.productType,
  ]);

  const today = new Date().toISOString().split('T')[0];
  const { data: notifications, isLoading, isError } = useQuery(
    ['rasff-deepdive', days],
    () => fetchRasff(days),
  );

  /* Any filter change also resets page to 0 — atomic single history entry. */
  const setFilter = useCallback((key: FilterKey, value: string | null) => {
    const urlKey = FILTER_KEY_TO_URL[key];
    patch({ [urlKey]: value, page: 0 } as Partial<UrlState>);
  }, [patch]);

  const clearFilters = useCallback(() => {
    patch({
      category: null, hazardCategory: null, hazard: null,
      class: null, risk: null, origin: null,
      notifying: null, dist: null, productType: null,
      page: 0,
    });
  }, [patch]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const activeFilters = activeFilterCount > 0;

  /* Mobile-only filters disclosure — pure UI toggle, intentionally NOT in the URL. */
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  /* ── Cascading filter options ── */
  const optCategory         = useMemo(() => countField(applyFilters(notifications || [], filters, 'category'),         'category'),         [notifications, filters]);
  const optHazardCategory   = useMemo(() => countField(applyFilters(notifications || [], filters, 'hazardCategory'),   'hazardCategory'),   [notifications, filters]);
  const optHazard           = useMemo(() => countField(applyFilters(notifications || [], filters, 'hazard'),           'hazard'),           [notifications, filters]);
  const optClassification   = useMemo(() => countField(applyFilters(notifications || [], filters, 'classification'),   'classification'),   [notifications, filters]);
  const optRiskDecision     = useMemo(() => countField(applyFilters(notifications || [], filters, 'riskDecision'),     'riskDecision'),     [notifications, filters]);
  const optOriginCountry    = useMemo(() => countField(applyFilters(notifications || [], filters, 'originCountry'),    'originCountry'),    [notifications, filters]);
  const optNotifyingCountry = useMemo(() => countField(applyFilters(notifications || [], filters, 'notifyingCountry'), 'notifyingCountry'), [notifications, filters]);
  const optDistCountry      = useMemo(() => countField(applyFilters(notifications || [], filters, 'distCountry'),      'distCountry'),      [notifications, filters]);
  const optProductType      = useMemo(() => countField(applyFilters(notifications || [], filters, 'productType'),      'productType'),      [notifications, filters]);

  /* ── Filtered (+ optional dateFrom / dateTo cap) ── */
  const filtered = useMemo(() => {
    let result = applyFilters(notifications || [], filters);
    if (dateFrom) {
      const start = new Date(dateFrom); start.setHours(0, 0, 0, 0);
      result = result.filter(n => !n.ec_validation_date || new Date(n.ec_validation_date) >= start);
    }
    if (dateTo) {
      const end = new Date(dateTo); end.setHours(23, 59, 59, 999);
      result = result.filter(n => !n.ec_validation_date || new Date(n.ec_validation_date) <= end);
    }
    return result;
  }, [notifications, filters, dateFrom, dateTo]);

  /* ── Search (applied after all filters) ── */
  const searched = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return filtered;
    return filtered.filter(n =>
      (n.subject              || '').toLowerCase().includes(q) ||
      (n.reference            || '').toLowerCase().includes(q) ||
      (n.hazard_names         || '').toLowerCase().includes(q) ||
      (n.notifying_country    || '').toLowerCase().includes(q) ||
      (n.origin_countries     || '').toLowerCase().includes(q) ||
      (n.product_category     || '').toLowerCase().includes(q) ||
      (n.dim_hazards?.hazardname || '').toLowerCase().includes(q)
    );
  }, [filtered, searchQuery]);

  /* ── Sorted ── */
  const sorted = useMemo(() => {
    const dir = sortAsc ? 1 : -1;
    return [...searched].sort((a, b) => {
      switch (sortCol) {
        case 'date':
          return dir * ((a.ec_validation_date || '').localeCompare(b.ec_validation_date || ''));
        case 'country':
          return dir * ((a.notifying_country || '').localeCompare(b.notifying_country || ''));
        case 'category':
          return dir * ((a.product_category || '').localeCompare(b.product_category || ''));
        case 'classification':
          return dir * ((a.classification || '').localeCompare(b.classification || ''));
        default: return 0;
      }
    });
  }, [searched, sortCol, sortAsc]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated  = useMemo(
    () => sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sorted, currentPage, pageSize],
  );

  const handleSearch = useCallback((q: string) => {
    set('q', q);                            // debounced URL write (mirrors to UI immediately)
    if (currentPage !== 0) patch({ page: 0 }); // but reset page right away
  }, [set, patch, currentPage]);

  const handleSort = useCallback((col: typeof sortCol) => {
    if (sortCol === col) set('dir', !sortAsc);
    else patch({ sort: col, dir: false });  // new column: start descending
  }, [sortCol, sortAsc, set, patch]);

  /* ── Previous-period data (2× window, only when toggle is on) ── */
  const { data: notifications2x, isLoading: isLoading2x } = useQuery(
    ['rasff-deepdive-prev', days],
    () => fetchRasff(days * 2),
    { enabled: comparePrev && days > 0 },
  );

  /* ── Chart data ── */
  const timelineData = useMemo(() => buildClassificationTimeline(filtered, effectiveBucket), [filtered, effectiveBucket]);
  const { bubbles: pairBubbles, topOrigins, topNotifying } = useMemo(() => buildCountryPairBubbles(filtered), [filtered]);

  /* ── Previous-period comparison ── */
  const prevPeriodData = useMemo(() => {
    if (!notifications2x || days === 0) return null;
    const now    = new Date();
    const cutoff = new Date(now.getTime() - days * 86_400_000);
    const start  = new Date(now.getTime() - 2 * days * 86_400_000);

    const prevRaw = notifications2x.filter(n =>
      n.ec_validation_date && new Date(n.ec_validation_date) < cutoff,
    );
    const prev = applyFilters(prevRaw, filters);

    const types = ['Alert', 'Border Rejection', 'Info (Attention)', 'Info (Follow-up)', 'Non-compliance'] as const;
    const rows = types
      .map(t => ({
        type: t,
        current:  filtered.filter(n => classifyType(n.classification) === t).length,
        previous: prev.filter(n => classifyType(n.classification) === t).length,
      }))
      .filter(r => r.current > 0 || r.previous > 0);

    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
    return {
      rows,
      currentLabel: `${fmt(cutoff)} – ${fmt(now)}`,
      prevLabel:    `${fmt(start)} – ${fmt(cutoff)}`,
    };
  }, [notifications2x, days, filtered, filters]);

  if (isLoading) return (
    <div className="rasff-deepdive">
      <div className="dash-card dash-card--full dash-card--hero">
        <div className="dash-card__body" style={{ padding: '2rem', textAlign: 'center' }}>Loading RASFF data…</div>
      </div>
    </div>
  );
  if (isError) return (
    <div className="rasff-deepdive">
      <div className="dash-card dash-card--full dash-card--hero">
        <div className="dash-card__body dash-card__msg--error" style={{ padding: '2rem', textAlign: 'center' }}>Failed to load RASFF data</div>
      </div>
    </div>
  );

  const sortIndicator = (col: typeof sortCol) =>
    sortCol === col ? (sortAsc ? ' ↑' : ' ↓') : '';

  return (
    <div className="rasff-deepdive">
      <h1 className="sr-only">RASFF Deep Dive — Food Safety Digest</h1>
      <div className="dash-card dash-card--full dash-card--hero">

        {/* ── Header ── */}
        <div className="dash-card__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <Link to="/" className="rasff-deepdive__back">← Dashboard</Link>
            <h3>RASFF Deep Dive</h3>
          </div>
          <div className="rasff-dash__header-right">
            <div className="rasff-dash__timeframe">
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                days={days}
                today={today}
                presets={TIME_FRAMES}
                onPreset={d => patch({ days: d, from: '', to: '' })}
                onRange={(from, to, apiDays) => patch({ from, to, days: apiDays })}
              />
            </div>
            <span className="rasff-dash__count">{filtered.length}</span>
            <span className="rasff-dash__count-label">notifications</span>
          </div>
        </div>

        {/* ── Filters ── */}
        {/* Mobile-only disclosure — hidden on desktop via CSS */}
        <button
          className="rasff-dash__filters-toggle"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
        >
          <span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}</span>
          <span className={`rasff-dash__toggle-icon ${filtersOpen ? 'rasff-dash__toggle-icon--open' : ''}`} />
        </button>
        <div className={`rasff-dash__filters${filtersOpen ? ' rasff-dash__filters--open' : ''}`}>
          <FilterSelect value={filters.category || ''} onChange={v => setFilter('category', v || null)} placeholder="All categories"
            options={optCategory.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
          <FilterSelect value={filters.hazardCategory || ''} onChange={v => setFilter('hazardCategory', v || null)} placeholder="All hazard categories"
            options={optHazardCategory.map(h => ({ value: h.name, label: `${h.name} (${h.value})` }))} />
          <FilterSelect value={filters.hazard || ''} onChange={v => setFilter('hazard', v || null)} placeholder="All hazards"
            options={optHazard.map(h => ({ value: h.name, label: `${h.name} (${h.value})` }))} />
          <FilterSelect value={filters.classification || ''} onChange={v => setFilter('classification', v || null)} placeholder="All classifications"
            options={optClassification.map(c => ({ value: c.name, label: `${getClassificationInfo(c.name).label} (${c.value})` }))} />
          <FilterSelect value={filters.riskDecision || ''} onChange={v => setFilter('riskDecision', v || null)} placeholder="All risk decisions"
            options={optRiskDecision.map(r => ({ value: r.name, label: `${r.name} (${r.value})` }))} />
          <FilterSelect value={filters.originCountry || ''} onChange={v => setFilter('originCountry', v || null)} placeholder="All origin countries"
            options={optOriginCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
          <FilterSelect value={filters.notifyingCountry || ''} onChange={v => setFilter('notifyingCountry', v || null)} placeholder="All notifying countries"
            options={optNotifyingCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
          <FilterSelect value={filters.distCountry || ''} onChange={v => setFilter('distCountry', v || null)} placeholder="All distribution countries"
            options={optDistCountry.map(c => ({ value: c.name, label: `${c.name} (${c.value})` }))} />
          <FilterSelect value={filters.productType || ''} onChange={v => setFilter('productType', v || null)} placeholder="All product types"
            options={optProductType.map(p => ({ value: p.name, label: `${p.name} (${p.value})` }))} />
          {activeFilters && (
            <button className="rasff-dash__clear" onClick={clearFilters}>Clear filters</button>
          )}
        </div>

        {/* ── Classification Timeline (full width) ── */}
        <div className="rasff-deepdive__section">
          <div className="rasff-deepdive__section-header">
            <h4>Classification Timeline</h4>
            <div className="rasff-dash__timeframe">
              <button
                className={`rasff-dash__tf-btn ${effectiveBucket === 'month' ? 'rasff-dash__tf-btn--active' : ''}`}
                onClick={() => set('bucket', 'month')}
              >By month</button>
              <button
                className={`rasff-dash__tf-btn ${effectiveBucket === 'week' ? 'rasff-dash__tf-btn--active' : ''}`}
                onClick={() => set('bucket', 'week')}
              >By week</button>
            </div>
          </div>
          {timelineData.length > 1 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={timelineData} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={11} tick={{ fill: 'var(--color-text-secondary)' }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} fontSize={11} tick={{ fill: 'var(--color-text-secondary)' }} />
                <Tooltip
                  content={({ label, payload }) => {
                    if (!payload?.length) return null;
                    // Filter out the "Alert trend" line — it duplicates the Alert bar entry
                    const bars = payload.filter((p: any) => p.name !== 'Alert trend' && p.value > 0);
                    if (!bars.length) return null;
                    return (
                      <div style={TOOLTIP_CONTENT_STYLE}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                        {bars.map((p: any) => (
                          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ color: 'var(--color-text-secondary)' }}>{p.dataKey}:</span>
                            <span style={{ fontWeight: 600 }}>{p.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 8 }} />
                <Bar dataKey="Border Rejection" stackId="a" fill={CLASS_COLORS['Border Rejection']} />
                <Bar dataKey="Info (Attention)"  stackId="a" fill={CLASS_COLORS['Info (Attention)']} />
                <Bar dataKey="Info (Follow-up)"  stackId="a" fill={CLASS_COLORS['Info (Follow-up)']} />
                <Bar dataKey="Non-compliance"    stackId="a" fill={CLASS_COLORS['Non-compliance']} />
                <Bar dataKey="Other"             stackId="a" fill={CLASS_COLORS['Other']} />
                <Bar dataKey="Alert"             stackId="a" fill={CLASS_COLORS['Alert']} />
                <Line
                  type="monotone" dataKey="Alert"
                  stroke={CLASS_COLORS['Alert']} strokeWidth={2}
                  strokeDasharray="4 2" dot={false}
                  name="Alert trend"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-card__msg">Select a longer time window to see trends</div>
          )}
        </div>

        {/* ── Row 2: Country pair bubbles + Year Comparison ── */}
        <div className="rasff-dash__charts rasff-dash__charts--row2">
          <div className="rasff-dash__chart-box">
            <h4>Origin × Notifying Country</h4>
            <p className="rasff-deepdive__chart-hint">
              Bubble size = frequency. EU countries (right side) catching third-country issues (top) reveal enforcement patterns.
            </p>
            {pairBubbles.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 80, left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number" dataKey="x" name="origin"
                    domain={[-0.5, topOrigins.length - 0.5]}
                    ticks={topOrigins.map((_, i) => i)}
                    tickFormatter={(i: number) => {
                      const n = topOrigins[i] || '';
                      return n.length > 12 ? n.slice(0, 11) + '…' : n;
                    }}
                    fontSize={10} angle={-35} textAnchor="end" interval={0}
                    tick={{ fill: 'var(--color-text-secondary)' }}
                    label={{ value: 'Origin country', position: 'insideBottom', offset: -60, fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  />
                  <YAxis
                    type="number" dataKey="y" name="notifying"
                    domain={[-0.5, topNotifying.length - 0.5]}
                    ticks={topNotifying.map((_, i) => i)}
                    tickFormatter={(i: number) => {
                      const n = topNotifying[i] || '';
                      return n.length > 14 ? n.slice(0, 13) + '…' : n;
                    }}
                    fontSize={10} width={115}
                    tick={{ fill: 'var(--color-text-secondary)' }}
                    label={{ value: 'Notifying country', angle: -90, position: 'insideLeft', offset: 10, fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  />
                  <ZAxis type="number" dataKey="z" range={[30, 600]} name="count" />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={TOOLTIP_CONTENT_STYLE}>
                          <div style={{ fontWeight: 600 }}>Origin: {d.originName}</div>
                          <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>Notified by: {d.notifyingName}</div>
                          <div style={{ marginTop: 4, fontWeight: 600 }}>{d.z} notifications</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={pairBubbles} fill="#2D6A4F" opacity={0.7} />
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <div className="dash-card__msg">Not enough data for this view</div>
            )}
          </div>

          <div className="rasff-dash__chart-box">
            <div className="rasff-deepdive__section-header" style={{ marginBottom: 'var(--space-sm)' }}>
              <h4>Previous Period</h4>
              <button
                className={`rasff-dash__tf-btn ${comparePrev ? 'rasff-dash__tf-btn--active' : ''}`}
                onClick={() => set('compare', !comparePrev)}
              >
                {comparePrev ? 'Hide' : 'Show'}
              </button>
            </div>
            {comparePrev && days === 0 && (
              <div className="dash-card__msg" style={{ paddingTop: 'var(--space-xl)' }}>
                Select a time window to compare periods
              </div>
            )}
            {comparePrev && days > 0 && isLoading2x && (
              <div className="dash-card__msg" style={{ paddingTop: 'var(--space-xl)' }}>Loading…</div>
            )}
            {comparePrev && days > 0 && prevPeriodData && (
              <>
                <div style={{ display: 'flex', gap: 'var(--space-lg)', fontSize: '0.72rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#1B4332', marginRight: 5, verticalAlign: 'middle' }} />{prevPeriodData.currentLabel}</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#74C69D', marginRight: 5, verticalAlign: 'middle' }} />{prevPeriodData.prevLabel}</span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={prevPeriodData.rows} margin={{ left: 0, right: 10, top: 5, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="type" fontSize={10}
                      tick={{ fill: 'var(--color-text-secondary)' }}
                      angle={-30} textAnchor="end" interval={0}
                    />
                    <YAxis allowDecimals={false} fontSize={11} tick={{ fill: 'var(--color-text-secondary)' }} width={30} />
                    <Tooltip
                      content={({ label, payload }) => {
                        if (!payload?.length) return null;
                        return (
                          <div style={TOOLTIP_CONTENT_STYLE}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                            {payload.map((p: any) => (
                              <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, display: 'inline-block', flexShrink: 0 }} />
                                <span style={{ color: 'var(--color-text-secondary)' }}>
                                  {p.dataKey === 'current' ? prevPeriodData.currentLabel : prevPeriodData.prevLabel}:
                                </span>
                                <span style={{ fontWeight: 600 }}>{p.value}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="current"  fill="#1B4332" radius={[3, 3, 0, 0]} maxBarSize={36} />
                    <Bar dataKey="previous" fill="#74C69D" radius={[3, 3, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
            {comparePrev && days > 0 && !isLoading2x && !prevPeriodData && (
              <div className="dash-card__msg" style={{ paddingTop: 'var(--space-xl)' }}>No data</div>
            )}
            {!comparePrev && (
              <div className="dash-card__msg" style={{ paddingTop: 'var(--space-xl)' }}>
                Toggle to compare with previous period
              </div>
            )}
          </div>
        </div>

        {/* ── Full notifications table ── */}
        <div className="rasff-dash__table-section">
          <div className="rasff-deepdive__table-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <span className="rasff-deepdive__table-title">
                Notifications
                <span className="rasff-deepdive__table-count">{searched.length}</span>
              </span>
            </div>
            <div className="rasff-deepdive__search-wrap">
              <span className="rasff-deepdive__search-icon">🔍</span>
              <input
                type="search"
                className="rasff-deepdive__search"
                placeholder="Search subject, reference, hazard, country…"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
              />
            </div>
            <div className="rasff-deepdive__table-controls">
              <select
                className="rasff-deepdive__page-size"
                value={pageSize}
                onChange={e => patch({ size: Number(e.target.value), page: 0 })}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s} per page</option>)}
              </select>
            </div>
          </div>

          <div className="rasff-dash__table-wrap">
            <table className="rasff-dash__table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('date')}    style={{ cursor: 'pointer' }}>Date{sortIndicator('date')}</th>
                  <th>Reference</th>
                  <th>Subject</th>
                  <th onClick={() => handleSort('country')} style={{ cursor: 'pointer' }}>Country{sortIndicator('country')}</th>
                  <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>Product Category{sortIndicator('category')}</th>
                  <th>Hazard Category</th>
                  <th>Hazard</th>
                  <th onClick={() => handleSort('classification')} style={{ cursor: 'pointer' }}>Classification{sortIndicator('classification')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(n => {
                  const cls = getClassificationInfo(n.classification);
                  return (
                    <tr key={n.rasff_id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{n.ec_validation_date ? formatISODate(n.ec_validation_date) : '—'}</td>
                      <td className="rasff-dash__ref">
                        <a href={`https://webgate.ec.europa.eu/rasff-window/screen/notification/${n.notif_id}`}
                          target="_blank" rel="noopener noreferrer">{n.reference}</a>
                      </td>
                      <td className="rasff-dash__subject">{n.subject}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{n.notifying_country || '—'}</td>
                      <td>{n.product_category || '—'}</td>
                      <td>{n.dim_hazards?.hazardscategoryname || '—'}</td>
                      <td>{n.dim_hazards?.hazardname || '—'}</td>
                      <td><span className={`rasff-badge ${cls.className}`}>{cls.label}</span></td>
                    </tr>
                  );
                })}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={8} className="dash-card__msg">No notifications match your search</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="rasff-deepdive__pagination">
            <button
              className="rasff-dash__tf-btn"
              onClick={() => set('page', 0)}
              disabled={currentPage === 0}
            >«</button>
            <button
              className="rasff-dash__tf-btn"
              onClick={() => set('page', Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            >‹ Prev</button>
            <span className="rasff-deepdive__pagination-info">
              Page {currentPage + 1} of {totalPages}
              <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                ({searched.length} results)
              </span>
            </span>
            <button
              className="rasff-dash__tf-btn"
              onClick={() => set('page', Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
            >Next ›</button>
            <button
              className="rasff-dash__tf-btn"
              onClick={() => set('page', totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >»</button>
          </div>
        </div>
      </div>

      {/* Subscribe CTA */}
      <div className="card" style={{ textAlign: 'center', padding: '28px 24px', marginTop: 20 }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-primary)', margin: '0 0 8px' }}>
          Get RASFF notifications in your inbox
        </h3>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', margin: '0 0 16px', lineHeight: 1.5 }}>
          Subscribe to daily alerts filtered by the product categories you care about.
        </p>
        <Link to="/subscribe/form" className="subscribe-card__cta" style={{ display: 'inline-block' }}>
          Subscribe Now
        </Link>
      </div>
    </div>
  );
}
